import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import {
  BON_MAX_AANTAL,
  BON_MAX_TOTAAL_BYTES,
  DECLARATIE_SOORT_LABEL,
  DeclaratieSoort,
  type DeclaratieStatus,
} from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { apiGet, apiPost, apiPut, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { TeamleiderKiezer } from '../components/declaraties/TeamleiderKiezer.js';
import { euro, formatDatum, STATUS_CHIP, STATUS_LABEL } from '../components/declaraties/labels.js';

/**
 * Kostendeclaraties - indienerskant. Vervangt het Microsoft Forms-formulier
 * (plan: docs/plans/declaraties.md): kostenregels + IBAN uit het
 * Word-formulier, bonnen-upload uit Forms, en het akkoord van de teamleider
 * gebeurt in de app (Beheer Declaraties, ook voor teamleiders).
 */

const invoerKlassen =
  'w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[14px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400';

const MAX_BON_MB = 10;
const MAX_TOTAAL_MB = BON_MAX_TOTAAL_BYTES / (1024 * 1024);
const BON_ACCEPT =
  '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,application/pdf,image/jpeg,image/png,image/webp';

interface RegelInvoer {
  datum: string;
  omschrijving: string;
  bedrag: string;
}

interface BonUpload {
  object: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

interface MineRij {
  id: string;
  declaratienummer: string;
  periode: string;
  totaalCent: number;
  vergoedingPercentage?: number;
  vergoedCent?: number;
  aantalBonnen: number;
  status: DeclaratieStatus;
  afwijzingsreden?: string;
  createdAt: string;
}

/** "12,50" / "12.50" / "12" → centen, of null bij onleesbare invoer. */
function parseEuro(v: string): number | null {
  const genormaliseerd = v.trim().replace(/€|\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(genormaliseerd)) return null;
  return Math.round(parseFloat(genormaliseerd) * 100);
}

const LEGE_REGEL: RegelInvoer = { datum: '', omschrijving: '', bedrag: '' };

/**
 * Keuze in de soort-lijst die alleen kantoorpersoneel ziet (verzoek Linda
 * 5 okt 2026). Geen DeclaratieSoort: hij stuurt `directNaarAdministratie`
 * mee, en de server weigert dat voor iedereen zonder isHeadOffice.
 */
const DIRECT = 'direct';

/** Waar de declaratie na indienen heen ging, voor de bevestiging bovenaan. */
type Bestemming = 'teamleider' | 'kantoor' | 'administratie' | 'administratie_mislukt';

export function DeclaratiesPage(): JSX.Element {
  const [periode, setPeriode] = useState('');
  const { user } = useAuth();
  const isKantoor = user?.isHeadOffice === true;
  const [soort, setSoort] = useState<DeclaratieSoort | typeof DIRECT | ''>('');
  const [bestemming, setBestemming] = useState<Bestemming>('teamleider');
  const [rekeningnummer, setRekeningnummer] = useState('');
  const [woonplaats, setWoonplaats] = useState('');
  // Leeg profiel = vraag of we de woonplaats ook daar bewaren (niet verplicht).
  const [profielZonderWoonplaats, setProfielZonderWoonplaats] = useState(false);
  const [woonplaatsOpslaan, setWoonplaatsOpslaan] = useState(false);
  const [regels, setRegels] = useState<RegelInvoer[]>([{ ...LEGE_REGEL }]);
  const [bonnen, setBonnen] = useState<BonUpload[]>([]);
  const [uploadBezig, setUploadBezig] = useState(false);
  const [fouten, setFouten] = useState<Record<string, string>>({});
  const [verstuurFout, setVerstuurFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  const [resultaat, setResultaat] = useState<string | null>(null);
  const [mine, setMine] = useState<MineRij[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const ibanAangeraakt = useRef(false);

  const laadMine = useCallback((): void => {
    void apiGet<{ declaraties: MineRij[] }>('/api/v1/declaraties/mine')
      .then((d) => setMine(d.declaraties))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    laadMine();
    // IBAN voorinvullen uit het profiel (besluit Jos 20 aug 2026); wie hier
    // iets anders typt, wint van het profiel voor déze declaratie.
    void apiGet<{ user: { rekeningnummer?: string; woonplaats?: string } }>('/api/v1/profile/me')
      .then((d) => {
        if (!ibanAangeraakt.current && d.user.rekeningnummer) {
          setRekeningnummer(d.user.rekeningnummer);
        }
        if (d.user.woonplaats) setWoonplaats((w) => w || (d.user.woonplaats ?? ''));
        else setProfielZonderWoonplaats(true);
      })
      .catch(() => undefined);
  }, [laadMine]);

  const totaalCent = regels.reduce((sum, r) => sum + (parseEuro(r.bedrag) ?? 0), 0);

  function wijzigRegel(index: number, patch: Partial<RegelInvoer>): void {
    setRegels((rs) => rs.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  async function uploadBestanden(files: FileList): Promise<void> {
    setVerstuurFout(null);
    const nieuw = [...files];
    if (bonnen.length + nieuw.length > BON_MAX_AANTAL) {
      setVerstuurFout(`Maximaal ${BON_MAX_AANTAL} bestanden per declaratie.`);
      return;
    }
    const totaal = [...bonnen, ...nieuw].reduce(
      (sum, b) => sum + ('sizeBytes' in b ? b.sizeBytes : b.size),
      0,
    );
    if (totaal > BON_MAX_TOTAAL_BYTES) {
      setVerstuurFout(`Alle bonnen samen mogen maximaal ${MAX_TOTAAL_MB} MB zijn.`);
      return;
    }
    setUploadBezig(true);
    try {
      for (const file of nieuw) {
        if (file.size > MAX_BON_MB * 1024 * 1024) {
          throw new Error(`${file.name} is groter dan ${MAX_BON_MB} MB.`);
        }
        const { uploadUrl, object } = await apiPost<{ uploadUrl: string; object: string }>(
          '/api/v1/declaraties/bon-upload-url',
          { filename: file.name, mimeType: file.type, sizeBytes: file.size },
        );
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'content-type': file.type },
          body: file,
        });
        if (!putRes.ok) throw new Error(`Upload van ${file.name} is mislukt.`);
        setBonnen((b) => [
          ...b,
          { object, filename: file.name, mimeType: file.type, sizeBytes: file.size },
        ]);
      }
    } catch (err) {
      setVerstuurFout(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'De upload is mislukt. Probeer het opnieuw.',
      );
    } finally {
      setUploadBezig(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function valideer(): Record<string, string> {
    const f: Record<string, string> = {};
    if (!periode.trim()) f.periode = 'Vul de periode in, bijvoorbeeld "juli 2026".';
    if (!rekeningnummer.trim()) f.rekeningnummer = 'Vul het IBAN in waarop je wilt ontvangen.';
    if (!woonplaats.trim())
      f.woonplaats =
        'Vul je woonplaats in; zonder woonplaats kan het boekhoudpakket de betaling niet aanmaken.';
    regels.forEach((r, i) => {
      if (!r.datum) f[`regel-${i}-datum`] = 'Kies een datum.';
      if (!r.omschrijving.trim()) f[`regel-${i}-omschrijving`] = 'Omschrijf de kosten.';
      if (parseEuro(r.bedrag) === null || parseEuro(r.bedrag) === 0) {
        f[`regel-${i}-bedrag`] = 'Vul een bedrag in, bijvoorbeeld 12,50.';
      }
    });
    return f;
  }

  async function verstuur(): Promise<void> {
    setBezig(true);
    setVerstuurFout(null);
    try {
      const direct = soort === DIRECT;
      const res = await apiPost<{ declaratienummer: string; status: DeclaratieStatus }>(
        '/api/v1/declaraties',
        {
          periode: periode.trim(),
          rekeningnummer: rekeningnummer.trim(),
          woonplaats: woonplaats.trim(),
          regels: regels.map((r) => ({
            datum: r.datum,
            omschrijving: r.omschrijving.trim(),
            bedragCent: parseEuro(r.bedrag) ?? 0,
          })),
          bonnen,
          ...(direct ? { directNaarAdministratie: true } : soort ? { soort } : {}),
        },
      );
      setResultaat(res.declaratienummer);
      if (woonplaatsOpslaan) {
        // ponytail: aparte PUT na een geslaagde indiening; mislukt hij, dan is
        // alleen het gemak weg en vraagt het formulier volgende keer opnieuw.
        try {
          await apiPut('/api/v1/profile', { woonplaats: woonplaats.trim() });
          setProfielZonderWoonplaats(false);
        } catch {
          /* niet blokkeren: de declaratie zelf is binnen */
        }
      }
      setBestemming(
        direct
          ? res.status === 'verstuurd'
            ? 'administratie'
            : 'administratie_mislukt'
          : soort
            ? 'kantoor'
            : 'teamleider',
      );
      setPeriode('');
      setSoort('');
      setRegels([{ ...LEGE_REGEL }]);
      setBonnen([]);
      setFouten({});
      laadMine();
      window.scrollTo({ top: 0 });
    } catch (err) {
      setVerstuurFout(
        err instanceof ApiError
          ? `${err.message} Je invoer staat nog in het formulier.`
          : 'Versturen is mislukt. Je invoer staat nog in het formulier; probeer het opnieuw.',
      );
    } finally {
      setBezig(false);
    }
  }

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    if (bezig || uploadBezig) return;
    const f = valideer();
    setFouten(f);
    if (Object.keys(f).length > 0) return;
    void verstuur();
  }

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-5">
        <h1 className="text-xl font-bold text-ink-900">Declaraties</h1>
        <p className="mt-1 text-[14px] text-ink-900/70">
          Declareer gemaakte kosten bij SUPPOHRT. Na akkoord van je teamleider wordt de declaratie
          naar de financiële administratie gestuurd en binnen 2 weken verwerkt. Een declaratie voor
          je VOG, of reiskosten zolang je nog geen NS-businesscard hebt, gaat rechtstreeks naar
          kantoor.
          {isKantoor &&
            ' Werk je op kantoor, dan kun je ook zonder akkoord direct naar de administratie declareren.'}
        </p>

        <TeamleiderKiezer />

        {resultaat && (
          <div className="mt-4 rounded-2xl border border-brand-100 bg-brand-50/60 p-5">
            <p className="text-[15px] font-semibold text-ink-900">
              Declaratie <span className="font-mono">{resultaat}</span> is ingediend.
            </p>
            <p className="mt-1 text-[14px] text-ink-900/70">
              {bestemming === 'administratie'
                ? 'De declaratie is direct naar de financiële administratie gestuurd. De status zie je hieronder bij je declaraties.'
                : bestemming === 'administratie_mislukt'
                  ? 'Direct versturen naar de administratie lukte niet. Een collega van kantoor krijgt een melding en stuurt de declaratie door.'
                  : `${bestemming === 'kantoor' ? 'Kantoor' : 'Je teamleider'} krijgt een melding en beoordeelt de declaratie. De status zie je hieronder bij je declaraties.`}
            </p>
          </div>
        )}

        <form onSubmit={onSubmit} noValidate className="mt-5 space-y-4">
          <div className="rounded-2xl border border-brand-100 bg-white p-5 sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="soort" className="text-[14px] font-semibold text-ink-900">
                  Soort declaratie
                </label>
                <select
                  id="soort"
                  value={soort}
                  onChange={(e) => setSoort(e.target.value as DeclaratieSoort | typeof DIRECT | '')}
                  className={`mt-1 ${invoerKlassen}`}
                >
                  <option value="">Overige kosten (naar je teamleider)</option>
                  {DeclaratieSoort.options.map((s) => (
                    <option key={s} value={s}>
                      {DECLARATIE_SOORT_LABEL[s]} (naar kantoor)
                    </option>
                  ))}
                  {isKantoor && (
                    <option value={DIRECT}>
                      Kantoorpersoneel: direct naar de administratie (geen akkoord nodig)
                    </option>
                  )}
                </select>
                {soort === DIRECT && (
                  <p className="mt-1 text-[12px] text-ink-900/50">
                    Je declaratie gaat meteen naar de financiële administratie, zonder akkoord van
                    een teamleider.
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="periode" className="text-[14px] font-semibold text-ink-900">
                  Periode <span className="text-red-600">*</span>
                </label>
                <input
                  id="periode"
                  type="text"
                  value={periode}
                  onChange={(e) => setPeriode(e.target.value)}
                  placeholder="bijv. juli 2026"
                  className={`mt-1 ${invoerKlassen}`}
                />
                {fouten.periode && (
                  <p className="mt-1 text-[13px] text-red-700">{fouten.periode}</p>
                )}
              </div>
              <div>
                <label htmlFor="iban" className="text-[14px] font-semibold text-ink-900">
                  IBAN <span className="text-red-600">*</span>
                </label>
                <input
                  id="iban"
                  type="text"
                  value={rekeningnummer}
                  onChange={(e) => {
                    ibanAangeraakt.current = true;
                    setRekeningnummer(e.target.value);
                  }}
                  placeholder="NL91 ABNA 0417 1643 00"
                  className={`mt-1 ${invoerKlassen}`}
                />
                <p className="mt-1 text-[12px] text-ink-900/50">
                  Vul je IBAN één keer in bij Mijn profiel, dan staat hij hier voortaan klaar.
                </p>
                {fouten.rekeningnummer && (
                  <p className="mt-1 text-[13px] text-red-700">{fouten.rekeningnummer}</p>
                )}
              </div>
              <div>
                <label htmlFor="woonplaats" className="text-[14px] font-semibold text-ink-900">
                  Woonplaats <span className="text-red-600">*</span>
                </label>
                <input
                  id="woonplaats"
                  type="text"
                  value={woonplaats}
                  onChange={(e) => setWoonplaats(e.target.value)}
                  placeholder="bijv. Utrecht"
                  autoComplete="address-level2"
                  className={`mt-1 ${invoerKlassen}`}
                />
                <p className="mt-1 text-[12px] text-ink-900/50">
                  Verplicht: het boekhoudpakket heeft je woonplaats nodig om de betaling aan te
                  maken.
                </p>
                {profielZonderWoonplaats ? (
                  <label className="mt-2 flex items-center gap-2 text-[13px] text-ink-900/70">
                    <input
                      type="checkbox"
                      checked={woonplaatsOpslaan}
                      onChange={(e) => setWoonplaatsOpslaan(e.target.checked)}
                      className="h-4 w-4 rounded border-brand-200 text-brand-600 focus:ring-brand-400"
                    />
                    Ook opslaan in mijn profiel, dan staat hij hier voortaan klaar
                  </label>
                ) : (
                  <p className="mt-1 text-[12px] text-ink-900/50">
                    Je woonplaats komt uit Mijn profiel; daar kun je hem aanpassen.
                  </p>
                )}
                {fouten.woonplaats && (
                  <p className="mt-1 text-[13px] text-red-700">{fouten.woonplaats}</p>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-brand-100 bg-white p-5 sm:p-6">
            <p className="text-[14px] font-semibold text-ink-900">
              Kostenregels <span className="text-red-600">*</span>
            </p>
            <div className="mt-3 space-y-3">
              {regels.map((r, i) => (
                <div key={i} className="grid gap-2 sm:grid-cols-[10rem_1fr_8rem_2.5rem]">
                  <div>
                    <input
                      type="date"
                      aria-label={`Datum regel ${i + 1}`}
                      value={r.datum}
                      onChange={(e) => wijzigRegel(i, { datum: e.target.value })}
                      className={invoerKlassen}
                    />
                    {fouten[`regel-${i}-datum`] && (
                      <p className="mt-1 text-[13px] text-red-700">{fouten[`regel-${i}-datum`]}</p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      aria-label={`Omschrijving regel ${i + 1}`}
                      value={r.omschrijving}
                      onChange={(e) => wijzigRegel(i, { omschrijving: e.target.value })}
                      placeholder="Omschrijving"
                      className={invoerKlassen}
                    />
                    {fouten[`regel-${i}-omschrijving`] && (
                      <p className="mt-1 text-[13px] text-red-700">
                        {fouten[`regel-${i}-omschrijving`]}
                      </p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label={`Bedrag regel ${i + 1}`}
                      value={r.bedrag}
                      onChange={(e) => wijzigRegel(i, { bedrag: e.target.value })}
                      placeholder="€ 0,00"
                      className={`${invoerKlassen} text-right`}
                    />
                    {fouten[`regel-${i}-bedrag`] && (
                      <p className="mt-1 text-[13px] text-red-700">{fouten[`regel-${i}-bedrag`]}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    aria-label={`Regel ${i + 1} verwijderen`}
                    disabled={regels.length === 1}
                    onClick={() => setRegels((rs) => rs.filter((_, j) => j !== i))}
                    className="self-start rounded-lg border border-brand-100 px-2 py-2 text-[14px] text-ink-900/60 hover:bg-brand-50 disabled:opacity-40"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between">
              <button
                type="button"
                disabled={regels.length >= 20}
                onClick={() => setRegels((rs) => [...rs, { ...LEGE_REGEL }])}
                className="rounded-lg border border-brand-100 px-3 py-1.5 text-[13px] font-medium text-ink-900/80 hover:bg-brand-50 disabled:opacity-40"
              >
                + Regel toevoegen
              </button>
              <p className="text-[14px] font-semibold tabular-nums text-ink-900">
                Totaal: {euro(totaalCent)}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-brand-100 bg-white p-5 sm:p-6">
            <p className="text-[14px] font-semibold text-ink-900">Bonnetjes of facturen</p>
            <p className="mt-1 text-[13px] text-ink-900/60">
              Maximaal {BON_MAX_AANTAL} bestanden, {MAX_BON_MB} MB per stuk en {MAX_TOTAAL_MB} MB
              samen (PDF, foto, Word of Excel).
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={BON_ACCEPT}
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) void uploadBestanden(e.target.files);
              }}
            />
            <button
              type="button"
              disabled={uploadBezig || bonnen.length >= BON_MAX_AANTAL}
              onClick={() => fileInputRef.current?.click()}
              className="mt-3 rounded-lg border border-brand-100 px-3 py-1.5 text-[13px] font-medium text-ink-900/80 hover:bg-brand-50 disabled:opacity-40"
            >
              {uploadBezig ? 'Bezig met uploaden…' : 'Bestand uploaden'}
            </button>
            {bonnen.length > 0 && (
              <ul className="mt-3 space-y-1">
                {bonnen.map((bon, i) => (
                  <li key={bon.object} className="flex items-center gap-2 text-[14px]">
                    <span className="text-ink-900">{bon.filename}</span>
                    <span className="text-[12px] text-ink-900/50">
                      ({Math.max(1, Math.round(bon.sizeBytes / 1024))} kB)
                    </span>
                    <button
                      type="button"
                      aria-label={`${bon.filename} verwijderen`}
                      onClick={() => setBonnen((b) => b.filter((_, j) => j !== i))}
                      className="text-[13px] text-red-700 hover:underline"
                    >
                      verwijderen
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {verstuurFout && (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-[14px] text-red-700">
              {verstuurFout}
            </p>
          )}

          <button
            type="submit"
            disabled={bezig || uploadBezig}
            className="rounded-lg bg-brand-600 px-5 py-3 text-[15px] font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {bezig ? 'Bezig met indienen…' : 'Declaratie indienen'}
          </button>
        </form>

        <section className="mt-10">
          <h2 className="text-[16px] font-bold text-ink-900">Mijn declaraties</h2>
          {mine.length === 0 ? (
            <p className="mt-2 text-[14px] text-ink-900/60">Nog geen declaraties ingediend.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {mine.map((rij) => (
                <li key={rij.id} className="rounded-2xl border border-brand-100 bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="font-mono text-[13px] text-ink-900/60">
                        {rij.declaratienummer}
                      </span>
                      <p className="text-[14px] text-ink-900">
                        {rij.periode} · {formatDatum(rij.createdAt)} · {rij.aantalBonnen}{' '}
                        {rij.aantalBonnen === 1 ? 'bon' : 'bonnen'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-right text-[14px] font-semibold tabular-nums text-ink-900">
                        {euro(rij.vergoedCent ?? rij.totaalCent)}
                        {rij.vergoedingPercentage && (
                          <span className="block text-[12px] font-normal text-ink-900/60">
                            {rij.vergoedingPercentage}% van {euro(rij.totaalCent)}
                          </span>
                        )}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[12px] font-medium ${STATUS_CHIP[rij.status]}`}
                      >
                        {STATUS_LABEL[rij.status]}
                      </span>
                    </div>
                  </div>
                  {rij.status === 'afgewezen' && rij.afwijzingsreden && (
                    <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700">
                      Reden: {rij.afwijzingsreden}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Layout>
  );
}
